import { useState, useCallback, useRef } from "react";
import { Request } from "../../../services/Request";
import { urls } from "../../../Utils/Urls";
import { getSessionIdentity } from "./sessionIdentity";

// Per-user recent-search history (dfs-personalization /recent-search/v1), same request shape as the
// mobile app: token plus RequestInfo.userInfo, limit in the query string, any logged-in role.
// Recording is a side-effect of the v3 search, fired at commit time by recordCommittedSearch.

const RECENT_LIMIT = 5;

const requestInfo = (identity) => ({
  userInfo: { tenantId: identity.tenantId, uuid: identity.uuid },
});

// `module` is a forward-compatible scope hint the backend ignores today.
const moduleCriteria = (module) => (module ? { RecentSearchCriteria: { module } } : {});

export const useRecentSearches = ({ module } = {}) => {
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  // Guards against overlapping fetches (focus can fire the effect repeatedly).
  const inFlight = useRef(false);

  const isLoggedIn = getSessionIdentity() !== null;

  const fetchRecent = useCallback(async () => {
    const identity = getSessionIdentity();
    if (!identity || inFlight.current) return;

    inFlight.current = true;
    setLoading(true);
    try {
      const response = await Request({
        url: urls.recent_search,
        params: { limit: RECENT_LIMIT },
        data: { ...moduleCriteria(module), RequestInfo: requestInfo(identity) },
      });
      // Request() resolves the error response on failure, so guard on status —
      // otherwise a 4xx/5xx would masquerade as "no history" and blank the list.
      const ok = response?.status >= 200 && response?.status < 300;
      const list = ok ? response?.data?.recentSearches : null;
      if (Array.isArray(list)) setRecentSearches(list);
    } catch (err) {
      // Best-effort: leave the current list untouched on failure.
      console.error("Recent search fetch error:", err);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [module]);

  const deleteOne = useCallback(async (searchId) => {
    const identity = getSessionIdentity();
    if (!identity || !searchId) return;

    // Optimistic removal; a later fetchRecent() reconciles if the call fails.
    setRecentSearches((prev) => prev.filter((item) => item.id !== searchId));
    try {
      await Request({
        url: urls.recent_search_delete,
        data: {
          searchId,
          ...moduleCriteria(module),
          RequestInfo: requestInfo(identity),
        },
      });
    } catch (err) {
      console.error("Recent search delete error:", err);
    }
  }, [module]);

  const clearAll = useCallback(async () => {
    const identity = getSessionIdentity();
    if (!identity) return;

    setRecentSearches([]);
    try {
      await Request({
        url: urls.recent_search_clear,
        data: {
          ...moduleCriteria(module),
          RequestInfo: requestInfo(identity),
        },
      });
    } catch (err) {
      console.error("Recent search clear error:", err);
    }
  }, [module]);

  return { recentSearches, loading, isLoggedIn, fetchRecent, deleteOne, clearAll };
};
